import React, { useCallback, useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";

import ClubbieButton from "../../components/ClubbieButton/ClubbieButton";
import useDebounce from "../../hooks/useDebounce";
import {
  createOrganisation,
  fetchOrganisations,
  getSelectedOrganisation,
  selectOrganisation,
  updateSelectedOrganisation,
} from "../../redux/slices/organisationSlice";
import { fixedValues } from "../../utils/fixedValues";
import { DASHBOARD_STACK } from "../../utils/screenNames";

const Organisation = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    organisations = [],
    loading,
    totalPages,
  } = useSelector((state) => state.organisation);

  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOption, setSelectedOption] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  // Fetch initial data and handle search
  useEffect(() => {
    const fetchData = async () => {
      const params = { page: 1, limit: 10 };
      if (!selectedOption && debouncedSearchTerm?.trim()) {
        params.search = debouncedSearchTerm;
      }
      await dispatch(fetchOrganisations(params));
    };
    fetchData();
  }, [dispatch, debouncedSearchTerm, selectedOption]);

  // Fetch selected organization
  useEffect(() => {
    const fetchSelectedOrg = async () => {
      try {
        const result = await dispatch(getSelectedOrganisation());
        if (result.payload) {
          setSelectedOption({
            value: result.payload.id,
            label: result.payload.name,
          });
        }
      } catch (error) {
      }
    };
    fetchSelectedOrg();
  }, [dispatch]);

  const handleInputChange = useCallback(
    (newValue, { action }) => {
      if (action === "input-change") {
        setSearchTerm(newValue);
        setSelectedOption(null);
        if (!newValue) {
          dispatch(fetchOrganisations({ page: 1, limit: 10 }));
          setPage(1);
        }
      }
    },
    [dispatch]
  );

  const handleCreateOrganisation = useCallback(
    async (name) => {
      try {
        setIsCreating(true);
        const createResult = await dispatch(createOrganisation({ name }));
        if (createResult.meta.requestStatus === "fulfilled") {
          const newOrg = createResult.payload;
          const newOption = { value: newOrg.id, label: newOrg.name };
          setSelectedOption(newOption);
          setSearchTerm(newOrg.name);
          toast.success(`Organisation "${newOrg.name}" created successfully!`);
        } else {
          throw new Error(
            createResult.payload || "Failed to create organisation"
          );
        }
      } catch (error) {
        toast.error(error.message || "Could not create organisation");
      } finally {
        setIsCreating(false);
      }
    },
    [dispatch]
  );

  const handleSelectChange = useCallback(
    (option) => {
      if (!option) {
        setSelectedOption(null);
        setSearchTerm("");
        return;
      }
      if (option.isCreate) {
        const orgName = option.label.replace('Create "', "").replace('"', "");
        handleCreateOrganisation(orgName);
      } else {
        setSelectedOption(option);
        setSearchTerm(option.label);
      }
    },
    [handleCreateOrganisation]
  );

  const handleLoadMore = useCallback(() => {
    if (page >= totalPages) return;
    const nextPage = page + 1;
    const params = {
      page: nextPage,
      limit: fixedValues.pagination.LIMIT,
      append: true,
    };
    if (searchTerm?.trim()) {
      params.search = searchTerm;
    }
    dispatch(fetchOrganisations(params));
    setPage(nextPage);
  }, [dispatch, page, searchTerm, totalPages]);

  const handleNextClick = useCallback(async () => {
    try {
      if (!selectedOption) {
        toast.error("Please select or create an organisation");
        return;
      }

      dispatch(
        selectOrganisation({
          id: selectedOption.value,
          name: selectedOption.label,
        })
      );

      const selectResult = await dispatch(
        updateSelectedOrganisation({
          id: selectedOption.value,
          updateData: {
            isSelected: true,
          },
        })
      );

      if (selectResult.meta.requestStatus === "fulfilled") {
        navigate(DASHBOARD_STACK.CREATE_TOURNAMENT.TOURNAMENT_NAME);
      } else {
        throw new Error(
          selectResult.payload || "Failed to select organisation"
        );
      }
    } catch (error) {
      toast.error(error?.message || "Something went wrong");
    }
  }, [dispatch, navigate, selectedOption]);

  const options = useMemo(() => {
    const baseOptions = organisations.map((org) => ({
      value: org.id,
      label: org.name,
      isCreate: false,
    }));

    if (
      searchTerm &&
      !selectedOption &&
      !organisations.some(
        (org) => org.name.toLowerCase() === searchTerm.toLowerCase()
      )
    ) {
      baseOptions.push({
        value: "new",
        label: `Create "${searchTerm}"`,
        isCreate: true,
      });
    }

    return baseOptions;
  }, [organisations, searchTerm, selectedOption]);

  const customStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        backgroundColor: "var(--BLUE-SHADE-8)",
        borderColor: "var(--BLUE-LINK)",
        borderRadius: "4px",
        minHeight: "45px",
        color: "var(--CLUBBIE-WHITE)",
        "&:hover": { borderColor: "var(--BLUE-LINK)" },
      }),
      input: (base) => ({ ...base, color: "var(--BLUE-LINK)" }),
      menu: (base) => ({
        ...base,
        backgroundColor: "var(--BLUE-SHADE-8)",
        border: `1px solid var(--BLUE-LINK)`,
        maxHeight: "200px",
        overflowY: "auto",
      }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused
          ? "var(--BLUE-SHADE-1)"
          : "var(--BLUE-SHADE-8)",
        color: "var(--BLUE-LINK)",
        "&:hover": { backgroundColor: "var(--BLUE-SHADE-1)" },
      }),
      singleValue: (base) => ({ ...base, color: "var(--BLUE-LINK)" }),
      placeholder: (base) => ({ ...base, color: "var(--BLUE-LINK)" }),
    }),
    []
  );

  const customMenuList = useCallback(
    (props) => (
      <div>
        {props.children}
        {page < totalPages && (
          <div
            className="text-center py-2 text-info"
            style={{ cursor: "pointer" }}
            onClick={handleLoadMore}
          >
            {loading ? "Loading..." : "Load more..."}
          </div>
        )}
      </div>
    ),
    [handleLoadMore, loading, page, totalPages]
  );

  return (
    <div className="d-flex flex-column align-items-center justify-content-center">
      <h4 className="text-[#FEFCEB] mb-4 Fsize_20">
        Select or Create Your Organisation
      </h4>
      <div className="w-100" style={{ minWidth: "400px" }}>
        <Select
          isClearable
          isSearchable
          isLoading={loading || isCreating}
          options={options}
          value={selectedOption}
          onInputChange={handleInputChange}
          onChange={handleSelectChange}
          placeholder="Search or create organisation..."
          styles={customStyles}
          noOptionsMessage={() =>
            loading ? "Loading..." : "No organisations found"
          }
          components={{ MenuList: customMenuList }}
          getOptionLabel={(option) => option.label}
          getOptionValue={(option) => option.value}
        />
      </div>
      <ClubbieButton
        className="clubbie-button mt-4"
        onClick={handleNextClick}
        disabled={isCreating || (!selectedOption && !searchTerm?.trim())}
        isLoading={isCreating}
      >
        Next
      </ClubbieButton>
    </div>
  );
};

export default Organisation;
